import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./GlossaryPage.css";

const glossary = [
  {
    category: "Trend",
    term: "EMA (Exponential Moving Average)",
    description: "A moving average that reacts faster to recent price changes. SignalFly uses EMA stacks and crossovers to identify trend direction and momentum shifts.",
    example: "EMA50 above EMA200 often supports bullish trend alignment."
  },
  {
    category: "Trend",
    term: "EMA Stack",
    description: "A structured order of short, medium, and long EMAs. A bullish stack means faster EMAs sit above slower EMAs; bearish is the opposite.",
    example: "EMA9 > EMA21 > EMA50 can show short-term strength."
  },
  {
    category: "Trend",
    term: "ADX",
    description: "Average Directional Index measures trend strength, not direction. Higher ADX means the market is trending more strongly.",
    example: "ADX above 20 or 25 is commonly used as a trend-strength filter."
  },
  {
    category: "Momentum",
    term: "RSI",
    description: "Relative Strength Index estimates momentum and overbought or oversold conditions. SignalFly uses it with other filters rather than as a standalone entry.",
    example: "RSI recovering from oversold can support a reversal setup."
  },
  {
    category: "Momentum",
    term: "Stoch RSI",
    description: "A faster oscillator derived from RSI. It helps detect momentum resets, extreme conditions, and early reversal pressure.",
    example: "A Stoch RSI reset can prepare a trend-continuation entry."
  },
  {
    category: "Volume",
    term: "VWAP",
    description: "Volume Weighted Average Price shows the average traded price weighted by volume. It is useful for intraday mean and institutional reference zones.",
    example: "Price reclaiming VWAP can confirm buyers are stepping in."
  },
  {
    category: "Volume",
    term: "Volume Rank",
    description: "A relative ranking of symbols by traded volume. It helps avoid weak or illiquid movers when scanning many pairs.",
    example: "Top ranked volume symbols may get priority in breakout dashboards."
  },
  {
    category: "Structure",
    term: "Support / Resistance",
    description: "Price zones where buyers or sellers previously reacted. SignalFly uses these levels for entries, targets, stop logic, and retests.",
    example: "A resistance breakout can become support during a retest."
  },
  {
    category: "Structure",
    term: "FVG (Fair Value Gap)",
    description: "An imbalance zone created by an aggressive move. Markets often revisit these areas before continuation or reversal.",
    example: "A bullish FVG may act as a demand zone during pullback."
  },
  {
    category: "Structure",
    term: "ICT Breaker",
    description: "A failed support or resistance zone that flips role after a structural shift. Breaker retests are used as continuation or reversal context.",
    example: "A broken demand zone can become a bearish breaker."
  },
  {
    category: "SignalFly",
    term: "MTF (Multi-Timeframe)",
    description: "A signal model that combines higher-timeframe context with lower-timeframe triggers to reduce noise and avoid isolated candle signals.",
    example: "1H trend, 15m setup, and 5m trigger can work together."
  },
  {
    category: "SignalFly",
    term: "Confluence",
    description: "Multiple independent conditions aligning at the same time. SignalFly dashboards group signals by confluence so the strongest setups are easier to scan.",
    example: "Trend alignment plus EMA band cross plus volume confirmation."
  },
  {
    category: "Risk",
    term: "Break Even",
    description: "A risk-management action that moves the stop level toward entry once profit has reached a configured threshold.",
    example: "After +4%, stop can move to entry or a protected level."
  },
  {
    category: "Risk",
    term: "Partial Take Profit",
    description: "Closing a configured percentage of the position after a profit threshold is reached, while leaving the remaining position active.",
    example: "Close 50% at +2%, then continue managing the rest."
  },
  {
    category: "Risk",
    term: "Trailing Stop Loss",
    description: "A stop level that follows price as profit grows. It helps protect gains without immediately closing the full trade.",
    example: "A callback TSL can trail behind price by a configured percentage."
  }
];

const categories = ["All", ...Array.from(new Set(glossary.map((item) => item.category)))];

export default function GlossaryPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const filteredGlossary = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return glossary.filter((item) => {
      const matchesCategory = category === "All" || item.category === category;
      const matchesQuery = !normalizedQuery
        || item.term.toLowerCase().includes(normalizedQuery)
        || item.description.toLowerCase().includes(normalizedQuery)
        || item.example.toLowerCase().includes(normalizedQuery);

      return matchesCategory && matchesQuery;
    });
  }, [category, query]);

  return (
    <div className="glossary-page">
      <header className="glossary-hero">
        <button className="glossary-back-btn" onClick={() => navigate("/")}>
          Back to Home
        </button>
        <span className="glossary-eyebrow">SignalFly Knowledge Base</span>
        <h1>Trading Glossary</h1>
        <p>
          A practical reference for the signals, filters, and risk-management terms used across
          SignalFly dashboards and trade setup screens.
        </p>
      </header>

      <section className="glossary-toolbar">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search terms, signals, or risk rules..."
        />
        <div className="glossary-category-tabs">
          {categories.map((categoryName) => (
            <button
              className={categoryName === category ? "active" : ""}
              key={categoryName}
              onClick={() => setCategory(categoryName)}
              type="button"
            >
              {categoryName}
            </button>
          ))}
        </div>
      </section>

      <section className="glossary-grid">
        {filteredGlossary.map((item) => (
          <article className="glossary-card" key={item.term}>
            <span className="glossary-tag">{item.category}</span>
            <h2>{item.term}</h2>
            <p>{item.description}</p>
            <div className="glossary-example">
              <strong>Example</strong>
              <span>{item.example}</span>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
